import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
  { code: "ht", label: "KR" },
];

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("chimen_lang", code);
  };

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/parcours", label: t("nav.parcours") },
    { to: "/carte", label: t("nav.map") },
    { to: "/sessions-live", label: t("nav.live") },
    { to: "/retraites", label: t("nav.retreats") },
    { to: "/temoignages", label: t("nav.testimonials") },
    { to: "/fondation", label: t("nav.foundation") },
  ];

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-200 ${isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"}`;

  return (
    <nav className="glass-nav sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo-fondation.jpg" alt="Fondation Sèlide Kouzen-Kouzin" className="w-9 h-9 rounded-full border border-primary/30 object-cover" />
          <div className="leading-tight">
            <span className="font-serif-display text-lg text-foreground block">Chimen Nanm</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-primary/80 block">Fondation Sèlide Kouzen-Kouzin</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Select value={i18n.language} onValueChange={changeLang}>
            <SelectTrigger className="w-[70px] h-8 bg-transparent border-primary/25 text-xs" data-testid="language-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === "admin" && (
            <Link to="/admin" className="text-sm text-secondary hover:underline underline-offset-4" data-testid="nav-admin-link">
              Admin
            </Link>
          )}
          {user ? (
            <Link to="/profil" className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors duration-200" data-testid="nav-profile-link">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-primary/40" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-xs text-primary">
                  {user.name?.[0]?.toUpperCase()}
                </span>
              )}
              {t("nav.profile")}
            </Link>
          ) : (
            <Link to="/auth" className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="nav-login-btn">
              {t("nav.login")}
            </Link>
          )}
        </div>

        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden px-6 pb-6 space-y-4 border-t border-primary/10 pt-4" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className="block text-sm text-foreground/80" onClick={() => setOpen(false)}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to={user ? "/profil" : "/auth"} className="block text-sm text-primary" onClick={() => setOpen(false)}>
            {user ? t("nav.profile") : t("nav.login")}
          </NavLink>
          <div className="flex gap-3">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => changeLang(l.code)}
                className={`text-xs px-3 py-1 rounded-full border ${i18n.language === l.code ? "border-primary text-primary" : "border-primary/20 text-foreground/60"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
